import React from 'react';

import { FormattedMessage, InjectedIntl, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { gridSize } from '@atlaskit/theme';
import Tooltip from '@atlaskit/tooltip';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import copy from '@atlassian/bitkit-copy/utils/clipboard-copy';

import { RelativeDate } from 'src/components/date';
import { JiraIssueLink } from 'src/components/jira/jira-issue-link/jira-issue-link';
import PageableTable, {
  TableCell,
  TableCellMobileHidden,
  TableHeaderMobileHidden,
} from 'src/components/pageable-table';
import { Pagination } from 'src/components/pagination';
import { LoadingStatus } from 'src/constants/loading-status';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useFlag } from 'src/hooks/flag';
import {
  openCreateBranchRepoDialog,
  setJiraIssue as setJiraIssueForCreateBranch,
} from 'src/redux/create-branch/actions';
import {
  changeFilterState,
  fetchJiraRelevantIssues,
  setIssueAssignee,
} from 'src/redux/jira/actions';
import { EmptyStateKind, IssueCategory } from 'src/redux/jira/constants';
import {
  getFilterState,
  getProjectAssociations,
  getRelevantIssues,
  getRelevantIssuesFetchedStatus,
  getRelevantIssuesPages,
  getRelevantProjectFetchedStatus,
  getRelevantSites,
} from 'src/redux/jira/selectors/repo-page-selectors';
import { JiraIssue, JiraIssueStatus, JiraUser } from 'src/redux/jira/types';
import { constructJqlFromFilter } from 'src/sections/repository/utils/construct-jql-from-filter';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { Page } from 'src/types/pagination';
import urls from 'src/urls/global';
import { publishUiEvent } from 'src/utils/analytics/publish';
import { captureMessageWithTags } from 'src/utils/sentry';

import { SCREEN_NAME } from '../constants';
import { ErrorState } from '../error-state';
import { convertFilterStateToQueryString } from '../relevant-issues/use-sync-filter-state-with-url/use-sync-filter-state-with-url';

import { DevActivityComponent } from './dev-activity';
import {
  NoAccessibleRelevantProjectsEmptyState,
  NoAccessibleRelevantSitesEmptyState,
} from './empty-state-no-accessible-relevant-projects';
import NoIssuesInRelevantProjectsEmptyState from './empty-state-no-issues-in-relevant-projects';
import NoMatchingIssuesEmptyState from './empty-state-no-matching-issues';
import NoRelevantProjectsEmptyState from './empty-state-no-relevant-projects';
import messages from './site-issues-list.i18n';
import * as styles from './site-issues-list.style';
import SiteIssuesSortableTableHeader from './site-issues-sortable-table-header';

export type SiteIssuesProps = {
  intl: InjectedIntl;
  emptyState: EmptyStateKind;
};

const SiteIssuesList = ({ intl, emptyState }: SiteIssuesProps) => {
  const dispatch = useDispatch();
  const relevantIssuesFetchedStatus = useSelector(
    getRelevantIssuesFetchedStatus
  );
  const { showSuccessFlag } = useFlag({ enableForPdvs: true });
  const relevantIssues = useSelector(getRelevantIssues);
  const relevantSites = useSelector(getRelevantSites);
  const filterState = useSelector(getFilterState);
  const numberOfPages = useSelector(getRelevantIssuesPages);
  const currentUser = useSelector(getCurrentUser);
  const relevantProjects = useSelector(getProjectAssociations)
    .map(projectAssociation => projectAssociation.project)
    .filter(project => project.site.cloudId === filterState.siteCloudId);
  const relevantProjectsFetchedStatus: LoadingStatus = useSelector(
    getRelevantProjectFetchedStatus
  );
  const isRelevantProjectsLoaded =
    filterState.siteCloudId &&
    relevantProjectsFetchedStatus === LoadingStatus.Success;
  const pages: Page[] = [];

  for (let page = 1; page <= numberOfPages; page++) {
    const qs = convertFilterStateToQueryString({
      ...filterState,
      currentPage: page,
    });

    pages.push({
      href: `${window.location.pathname}?${qs}`,
      label: page,
    });
  }

  const useBbjsNewIssueSearch = useStatsigGate(
    StatsigFeatureKeys.useBbjsNewIssueSearch
  );

  const isLoading =
    relevantIssuesFetchedStatus === LoadingStatus.Fetching ||
    relevantIssuesFetchedStatus === LoadingStatus.Before;
  const isFailed = relevantIssuesFetchedStatus === LoadingStatus.Failed;
  const hasEmptyState = emptyState !== EmptyStateKind.None;

  const filtersApplied =
    filterState.projectIds.length ||
    filterState.issueCategories.length ||
    filterState.textFilterQuery ||
    filterState.assignees.length;

  const notShowingAnyIssues =
    hasEmptyState || isFailed || isLoading || relevantIssues.length === 0;

  const selectedSite = relevantSites.find(
    s => s.cloudId === filterState.siteCloudId
  );

  const getEmptyStateContent = () => {
    if (hasEmptyState) {
      switch (emptyState) {
        case EmptyStateKind.HasNoRelevantProjects:
          return <NoRelevantProjectsEmptyState />;
        case EmptyStateKind.HasNoAccessibleRelevantProjects:
          return <NoAccessibleRelevantProjectsEmptyState />;
        case EmptyStateKind.HasNoAccessibleRelevantSites:
          return <NoAccessibleRelevantSitesEmptyState />;
        default:
          captureMessageWithTags(
            `Unexpected empty state '${emptyState}' on the Jira issue list`
          );
          return null;
      }
    } else if (isFailed) {
      return (
        <ErrorState
          message={messages.failedToLoadIssuesTitle}
          onRetry={() => dispatch(fetchJiraRelevantIssues())}
        />
      );
    }

    if (filtersApplied) {
      return <NoMatchingIssuesEmptyState site={selectedSite} />;
    }
    return <NoIssuesInRelevantProjectsEmptyState site={selectedSite} />;
  };

  const renderEmptyState = () => (
    <tr>
      <TableCell centered colSpan={5}>
        <styles.EmptyStateWrapper>
          {getEmptyStateContent()}
        </styles.EmptyStateWrapper>
      </TableCell>
    </tr>
  );

  const renderHeaders = () => {
    return (
      <styles.TableHeaders>
        <SiteIssuesSortableTableHeader
          label={intl.formatMessage(messages.issueHeader)}
          field="key"
          isDisabled={notShowingAnyIssues}
          dataIssueColumn="issue-summary-column"
        />
        <SiteIssuesSortableTableHeader
          width={`${gridSize() * 14}px`}
          label={intl.formatMessage(messages.statusHeader)}
          field="status"
          isDisabled={notShowingAnyIssues}
        />
        <SiteIssuesSortableTableHeader
          mobileHidden
          width={`${gridSize() * 11}px`}
          label={intl.formatMessage(messages.assigneeHeader)}
          field="assignee"
          isDisabled={notShowingAnyIssues}
          dataIssueColumn="issue-assignee-column"
        />
        <SiteIssuesSortableTableHeader
          mobileHidden
          width={`${gridSize() * 13}px`}
          label={intl.formatMessage(messages.updatedHeader)}
          field="updated"
          isDisabled={notShowingAnyIssues}
        />
        <TableHeaderMobileHidden width={`${gridSize() * 7}px`}>
          <FormattedMessage {...messages.actionsHeader} />
        </TableHeaderMobileHidden>
      </styles.TableHeaders>
    );
  };

  const renderSummary = (jiraIssue: JiraIssue, cloudUrl: string) => {
    const uiEvent = {
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'jiraTabIssue',
      source: 'jiraTabIssuesTable',
    };
    return (
      <styles.SummaryContainer>
        <styles.SummaryInnerContainer>
          <JiraIssueLink
            jiraIssue={jiraIssue}
            jiraUrl={cloudUrl}
            uiEvent={uiEvent}
          />
        </styles.SummaryInnerContainer>
        <DevActivityComponent
          cloudId={jiraIssue.site.cloudId}
          issueId={jiraIssue.id}
        />
      </styles.SummaryContainer>
    );
  };

  const appearanceFromStatusCategory = (category: IssueCategory) => {
    switch (category) {
      case IssueCategory.InProgress:
        return 'inprogress';
      case IssueCategory.Done:
        return 'success';
      default:
        return 'default';
    }
  };

  const renderStatus = (status: JiraIssueStatus) => {
    return (
      <Tooltip content={status.name}>
        <Lozenge appearance={appearanceFromStatusCategory(status.category)}>
          {status.name}
        </Lozenge>
      </Tooltip>
    );
  };

  const renderAssignee = (assignee: JiraUser | undefined) => {
    if (assignee) {
      return (
        <Tooltip content={assignee.displayName}>
          <Avatar
            name={assignee.displayName}
            appearance="circle"
            size="small"
            src={assignee.avatarUrls['128x128'] || assignee.avatarUrls['48x48']}
          />
        </Tooltip>
      );
    }

    return undefined;
  };

  const renderUpdated = (updated: string | undefined) => {
    if (updated) {
      return <RelativeDate date={updated} />;
    }
    return '';
  };
  const createBranchUrl = (jiraIssue: JiraIssue) =>
    urls.ui.createBranch(
      jiraIssue.key,
      jiraIssue.issueType.name,
      jiraIssue.summary
    );

  const handleCreateBranchClick = (jiraIssue: JiraIssue) => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'jiraTabIssueCreateBranch',
      source: 'jiraTabIssuesTable',
    });

    dispatch(openCreateBranchRepoDialog());
    dispatch(
      setJiraIssueForCreateBranch({
        key: jiraIssue.key,
        summary: jiraIssue.summary,
        type: jiraIssue.issueType.name,
      })
    );
  };

  const handleCopyIssueLinkClick = (jiraUrl: string) => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'jiraTabIssueCopyLink',
      source: 'jiraTabIssuesTable',
    });
    copy(jiraUrl);
    showSuccessFlag(
      intl.formatMessage(messages.issueLinkCopiedBody),
      intl.formatMessage(messages.issueLinkCopiedTitle)
    );
  };

  const handleOpenInJiraClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'jiraTabIssueOpenInJira',
      source: 'jiraTabIssuesTable',
    });
  };

  const handleViewMoreInJiraClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'jiraTabViewMoreIssueInJira',
      source: 'jiraTabIssuesTable',
    });
  };

  const handleAssignToMe = (issueKey: string) => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'jiraTabIssueAssignToMe',
      source: 'jiraTabIssuesTable',
    });
    if (currentUser?.account_id) {
      dispatch(setIssueAssignee(issueKey, currentUser.account_id));
    }
  };

  const renderActions = (jiraIssue: JiraIssue) => {
    const jiraUrl = `${jiraIssue.site.cloudUrl}/browse/${jiraIssue.key}`;
    return (
      <DropdownMenu
        trigger={({ triggerRef, ...props }) => (
          <Button
            {...props}
            appearance="subtle"
            iconBefore={
              <MoreIcon label={intl.formatMessage(messages.moreLabel)} />
            }
            ref={triggerRef}
          />
        )}
        shouldFlip={false}
        placement="bottom-end"
        testId="issues-dropdown"
      >
        <DropdownItemGroup title={intl.formatMessage(messages.bitbucket)}>
          <DropdownItem
            href={createBranchUrl(jiraIssue)}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              handleCreateBranchClick(jiraIssue);
            }}
            testId="issues-dropdown-create-branch"
          >
            <FormattedMessage {...messages.createBranchButton} />
          </DropdownItem>
        </DropdownItemGroup>
        <DropdownItemGroup title={intl.formatMessage(messages.jira)}>
          {currentUser &&
            jiraIssue.assignee?.accountId !== currentUser?.account_id && (
              <DropdownItem
                onClick={(event: React.MouseEvent) => {
                  event.preventDefault();
                  handleAssignToMe(jiraIssue.key);
                }}
                testId="issues-dropdown-assign-to-me"
              >
                <FormattedMessage {...messages.assignToMe} />
              </DropdownItem>
            )}
          <DropdownItem
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              handleCopyIssueLinkClick(jiraUrl);
            }}
            testId="issues-dropdown-copy-issue-link"
          >
            <FormattedMessage {...messages.copyIssueLink} />
          </DropdownItem>
          <DropdownItem
            href={jiraUrl}
            target="_blank"
            onClick={handleOpenInJiraClick}
            testId="issues-dropdown-open-jira"
          >
            <FormattedMessage {...messages.openInJira} />
          </DropdownItem>
        </DropdownItemGroup>
      </DropdownMenu>
    );
  };

  const renderRow = (issue: JiraIssue) => (
    <styles.TableRow key={issue.key} data-testid="repo-jira-tab-issue-row">
      <TableCell
        data-testid="repo-jira-tab-issue-summary"
        data-issue-column="issue-summary-column"
      >
        {renderSummary(issue, issue.site.cloudUrl)}
      </TableCell>
      <TableCell data-testid="repo-jira-tab-issue-status">
        {renderStatus(issue.status!)}
      </TableCell>
      <TableCellMobileHidden
        data-testid="repo-jira-tab-issue-assignee"
        data-issue-column="issue-assignee-column"
      >
        <styles.AssigneeWrapper>
          {renderAssignee(issue.assignee)}
        </styles.AssigneeWrapper>
      </TableCellMobileHidden>
      <TableCellMobileHidden data-testid="repo-jira-tab-issue-updated">
        {renderUpdated(issue.updated)}
      </TableCellMobileHidden>
      <TableCellMobileHidden data-testid="repo-jira-tab-issue-actions">
        <styles.ActionsWrapper>{renderActions(issue)}</styles.ActionsWrapper>
      </TableCellMobileHidden>
    </styles.TableRow>
  );

  const handlePageChanged = (page: Page) => {
    dispatch(
      changeFilterState({
        currentPage: page.label as number,
      })
    );
  };

  const renderViewMoreInJira = () => {
    if (!selectedSite) return null;
    const baseUrl = selectedSite.cloudUrl;
    const relevantProjectsForJql = isRelevantProjectsLoaded
      ? relevantProjects
      : [];
    const jql = constructJqlFromFilter(filterState, relevantProjectsForJql);
    return (
      <Link
        target="_blank"
        href={`${baseUrl}/issues/?jql=${jql}`}
        onClick={handleViewMoreInJiraClick}
      >
        {intl.formatMessage(messages.viewMoreInJiraLink)}
      </Link>
    );
  };

  return useBbjsNewIssueSearch ? (
    <styles.TableContainer>
      <PageableTable
        renderEmptyState={renderEmptyState}
        renderHeaders={renderHeaders}
        rows={relevantIssues}
        isLoading={!hasEmptyState && isLoading}
      >
        {jiraIssue => renderRow(jiraIssue)}
      </PageableTable>
      {!notShowingAnyIssues && !isLoading && (
        <Box
          xcss={xcss({
            paddingBlockStart: 'space.100',
            paddingLeft: 'space.050',
          })}
        >
          {renderViewMoreInJira()}
        </Box>
      )}
    </styles.TableContainer>
  ) : (
    <styles.TableContainer>
      <PageableTable
        renderEmptyState={renderEmptyState}
        renderHeaders={renderHeaders}
        rows={relevantIssues}
        // Show the loading state on the table only if we are not displaying an empty state
        isLoading={!hasEmptyState && isLoading}
        renderPagination={() => (
          <styles.PaginationWrapper>
            <Pagination
              isError={isFailed}
              isLoading={!hasEmptyState && isLoading}
              page={filterState.currentPage}
              pages={pages}
              onPageChange={handlePageChanged}
              publishEvent={false}
              uiEventSource={SCREEN_NAME}
              uiEventSubjectId="jiraIssuesPagination"
            />
          </styles.PaginationWrapper>
        )}
      >
        {jiraIssue => renderRow(jiraIssue)}
      </PageableTable>
    </styles.TableContainer>
  );
};

export default injectIntl(SiteIssuesList);
